import { useEffect, useState, useMemo, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, MapPin, User, Download, FileText, Bell, List, CalendarDays, FileAudio, FileVideo, ImageIcon, File as FileIcon } from 'lucide-react';

interface Horario {
  id: number;
  dia: string;
  horaInicio: string;
  horaFin: string;
  materia: string;
  materiaCode?: string;
  aula: string;
  docente: string;
}

interface CalendarEvent {
  id: string;
  title: string;
  start?: string;
  end?: string;
  daysOfWeek?: number[];
  startTime?: string;
  endTime?: string;
  startRecur?: string;
  endRecur?: string;
  backgroundColor?: string;
  borderColor?: string;
  extendedProps: {
    classroom: string;
    professor: string;
  };
}

interface Material {
  id: number;
  materia: string;
  titulo: string;
  descripcion?: string;
  archivo: string;
  archivoTipo?: string;
  archivoSize?: number;
  fechaSubida: string;
  subioPor?: string;
  periodoNombre?: string;
}

interface Aviso {
  id: number;
  titulo: string;
  mensaje: string;
  tipo: 'informativo' | 'importante' | 'urgente';
  fecha: string;
}

type ViewMode = 'list' | 'calendar';

function getMaterialFileIcon(mimeType?: string) {
  if (!mimeType) return <FileIcon className="w-4 h-4 text-gray-400" />;
  if (mimeType.startsWith('image/')) return <ImageIcon className="w-4 h-4 text-blue-500" />;
  if (mimeType.startsWith('audio/')) return <FileAudio className="w-4 h-4 text-green-500" />;
  if (mimeType.startsWith('video/')) return <FileVideo className="w-4 h-4 text-purple-500" />;
  if (mimeType === 'application/pdf') return <FileText className="w-4 h-4 text-red-500" />;
  return <FileText className="w-4 h-4 text-blue-600" />;
}

function isPreviewable(mimeType?: string): boolean {
  return /^(image\/|audio\/|video\/|application\/pdf)/.test(mimeType || '');
}

export default function AlumnoHorarios() {
  const [horarios, setHorarios] = useState<Horario[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [materiales, setMateriales] = useState<Material[]>([]);
  const [avisos, setAvisos] = useState<Aviso[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('calendar');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [horariosRes, calendarRes, materialesRes, avisosRes] = await Promise.all([
        fetch('/api/alumnos/me/horarios', { credentials: 'include' }),
        fetch('/api/alumnos/me/horarios/calendar', { credentials: 'include' }),
        fetch('/api/alumnos/me/materiales', { credentials: 'include' }),
        fetch('/api/alumnos/me/avisos', { credentials: 'include' })
      ]);

      if (horariosRes.ok) {
        const data = await horariosRes.json();
        setHorarios(data.horarios || []);
      }

      if (calendarRes.ok) {
        const data = await calendarRes.json();
        setCalendarEvents(data.events || []);
      }

      if (materialesRes.ok) {
        const data = await materialesRes.json();
        setMateriales(data.materiales || []);
      }

      if (avisosRes.ok) {
        const data = await avisosRes.json();
        setAvisos(data.avisos || []);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadMaterial = useCallback(async (materialId: number, archivo: string) => {
    try {
      const response = await fetch(`/api/alumnos/me/materiales/${materialId}/download`, {
        credentials: 'include',
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = archivo;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (err) {
      console.error('Error descargando material:', err);
    }
  }, []);

  const diasOrden = useMemo(() => ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'], []);

  const horariosPorDia = useMemo(() => {
    return diasOrden.reduce((acc, dia) => {
      acc[dia] = horarios.filter(h => 
        h.dia.toLowerCase() === dia.toLowerCase()
      ).sort((a, b) => 
        a.horaInicio.localeCompare(b.horaInicio)
      );
      return acc;
    }, {} as Record<string, Horario[]>);
  }, [horarios, diasOrden]);


  const getAvisoColor = useCallback((tipo: string) => {
    switch (tipo) {
      case 'urgente':
        return 'bg-red-50 border-red-200 text-red-900';
      case 'importante':
        return 'bg-yellow-50 border-yellow-200 text-yellow-900';
      default:
        return 'bg-blue-50 border-blue-200 text-blue-900';
    }
  }, []);

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Horarios y Materiales</h1>
        <p className="text-gray-600 mt-2">Consulta tu horario, descarga materiales y revisa avisos</p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {avisos.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <Bell className="w-5 h-5 mr-2 text-blue-600" />
            Avisos Relevantes
          </h2>
          {avisos.map((aviso) => (
            <div 
              key={aviso.id} 
              className={`border rounded-lg p-4 ${getAvisoColor(aviso.tipo)}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-sm">{aviso.titulo}</h3>
                  <p className="text-sm mt-1">{aviso.mensaje}</p>
                </div>
                <span className="text-xs ml-3 flex-shrink-0">
                  {new Date(aviso.fecha).toLocaleDateString()}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-blue-600" />
                Horario de Clases
              </CardTitle>
              <CardDescription>
                Tu calendario semanal con horarios, aulas y docentes
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant={viewMode === 'calendar' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('calendar')}
              >
                <CalendarDays className="w-4 h-4 mr-1" />
                Calendario
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('list')}
              >
                <List className="w-4 h-4 mr-1" />
                Lista
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {horarios.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p>No hay horarios registrados</p>
            </div>
          ) : viewMode === 'calendar' ? (
            <div className="bg-white rounded-lg">
              <FullCalendar
                plugins={[dayGridPlugin, timeGridPlugin]}
                initialView="timeGridWeek"
                headerToolbar={{
                  left: 'prev,next today',
                  center: 'title',
                  right: 'timeGridWeek,timeGridDay'
                }}
                locale="es"
                events={calendarEvents}
                height="auto"
                slotMinTime="06:00:00"
                slotMaxTime="22:00:00"
                allDaySlot={false}
                weekends={true}
                nowIndicator={true}
                eventContent={(eventInfo) => (
                  <div className="p-1 text-xs overflow-hidden">
                    <div className="font-semibold truncate">{eventInfo.event.title}</div>
                    <div className="flex items-center gap-1 text-blue-100 truncate">
                      <MapPin className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{eventInfo.event.extendedProps.classroom}</span>
                    </div>
                    <div className="flex items-center gap-1 text-blue-100 truncate">
                      <User className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{eventInfo.event.extendedProps.professor}</span>
                    </div>
                  </div>
                )}
                buttonText={{
                  today: 'Hoy',
                  week: 'Semana',
                  day: 'Día'
                }}
              />
            </div>
          ) : (
            <div className="space-y-6">
              {diasOrden.map((dia) => {
                const clasesDelDia = horariosPorDia[dia];
                if (!clasesDelDia || clasesDelDia.length === 0) return null;

                return (
                  <div key={dia}>
                    <h3 className="font-semibold text-lg text-gray-900 mb-3 border-b pb-2">{dia}</h3>
                    <div className="space-y-3">
                      {clasesDelDia.map((horario) => (
                        <div 
                          key={horario.id} 
                          className="bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
                        >
                          <h4 className="font-semibold text-blue-700">{horario.materia}</h4>
                          <div className="mt-2 space-y-1 text-sm text-gray-600">
                            <div className="flex items-center">
                              <Clock className="w-4 h-4 mr-2 text-gray-400" />
                              {horario.horaInicio} - {horario.horaFin}
                            </div>
                            <div className="flex items-center">
                              <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                              {horario.aula}
                            </div>
                            <div className="flex items-center">
                              <User className="w-4 h-4 mr-2 text-gray-400" />
                              {horario.docente}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <FileText className="w-5 h-5 mr-2 text-blue-600" />
            Materiales de Clase
          </CardTitle>
          <CardDescription>
            Visualiza y descarga materiales, apuntes y recursos educativos
          </CardDescription>
        </CardHeader>
        <CardContent>
          {materiales.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p>No hay materiales disponibles</p>
            </div>
          ) : (
            <div className="space-y-3">
              {materiales.map((material) => (
                <div key={material.id} className="flex items-center justify-between border rounded-lg p-4 gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    {getMaterialFileIcon(material.archivoTipo)}
                    <div className="min-w-0">
                      <h4 className="font-medium text-gray-900 truncate">{material.titulo}</h4>
                      <p className="text-xs text-gray-500 truncate">{material.materia}{material.periodoNombre ? ` · ${material.periodoNombre}` : ''}</p>
                      {material.descripcion && (
                        <p className="text-xs text-gray-600 mt-0.5">{material.descripcion}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-0.5">{material.archivo}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadMaterial(material.id, material.archivo)}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Descargar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
}
